import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import Actions from '../store/Actions';
import {
  UserResponse,
  OrganizationResponse,
  LoginResponse,
} from '../types/generatedTypes';

interface AuthError {
  message: string;
  type:
    | 'network'
    | 'validation'
    | 'authentication'
    | 'authorization'
    | 'server'
    | 'unknown';
  code?: string;
  details?: Record<string, unknown>;
}

interface HttpErrorResponse {
  response?: {
    status: number;
    data?: unknown;
  };
  message?: string;
}

interface AuthState {
  user: UserResponse | null;
  organization: OrganizationResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  rememberMe: boolean;
  error: AuthError | null;
  token: string | null;

  // Actions
  login: (
    email: string,
    password: string,
    rememberMe: boolean
  ) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  getCurrentUser: () => Promise<void>;
  setToken: (token: string | null) => void;
  initializeAuth: () => Promise<void>;
  setError: (error: AuthError) => void;
}

const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      user: null,
      organization: null,
      isAuthenticated: false,
      isLoading: false,
      rememberMe: false,
      error: null,
      token:
        typeof window !== 'undefined'
          ? localStorage.getItem('auth_token')
          : null,

      setToken: (token: string | null) => {
        if (token) {
          localStorage.setItem('auth_token', token);
        } else {
          localStorage.removeItem('auth_token');
        }
        set({ token });
      },

      login: async (email: string, password: string, rememberMe: boolean) => {
        set({ isLoading: true, error: null });

        try {
          const data: LoginResponse = await Actions.login(email, password);

          // Store token
          get().setToken(data.access_token);

          // Store remember me preference
          if (rememberMe) {
            localStorage.setItem('remember_me', 'true');
          } else {
            localStorage.removeItem('remember_me');
          }

          set({
            user: {
              id: data.user.id,
              email: data.user.email,
              name: data.user.name,
              organization_id: data.user.organization_id,
            },
            organization: {
              id: data.organization.id,
              name: data.organization.name,
            },
            isAuthenticated: true,
            isLoading: false,
            rememberMe: rememberMe,
            error: null,
          });
        } catch (error) {
          // Extract backend error message from response
          const httpError = error as HttpErrorResponse;
          const backendMessage =
            httpError?.response?.data &&
            typeof httpError.response.data === 'object' &&
            'detail' in httpError.response.data
              ? (httpError.response.data as { detail: string }).detail
              : error instanceof Error
                ? error.message
                : 'Login failed';

          const authError: AuthError = {
            message: backendMessage,
            type:
              httpError?.response?.status === 401
                ? 'authentication'
                : error instanceof Error && error.message.includes('network')
                  ? 'network'
                  : 'unknown',
          };

          set({
            isLoading: false,
            error: authError,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          const token = get().token;
          if (token) {
            // Call logout endpoint
            await Actions.logout();
          }
        } catch (error) {
          console.error('Logout API call failed:', error);
        } finally {
          // Clear local state regardless of API call success
          get().setToken(null);
          localStorage.removeItem('remember_me');
          set({
            user: null,
            organization: null,
            isAuthenticated: false,
            rememberMe: false,
            error: null,
          });
        }
      },

      getCurrentUser: async () => {
        const token = get().token;
        if (!token) {
          set({ isAuthenticated: false });
          return;
        }

        try {
          set({ isLoading: true });

          const data = await Actions.getCurrentUser();
          console.log(data);

          set({
            user: data.user,
            organization: data.organization,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: unknown) {
          console.error('Failed to get current user:', error);

          // Only logout on authentication/authorization errors (401)
          if ((error as HttpErrorResponse)?.response?.status === 401) {
            get().setToken(null);
            set({
              user: null,
              organization: null,
              isAuthenticated: false,
              isLoading: false,
              error: {
                message: 'Session expired',
                type: 'authentication',
              },
            });
          } else {
            // For other errors (500, network issues, etc.), keep user logged in
            set({
              isLoading: false,
              error: {
                message: 'Failed to fetch user data. Please try again.',
                type: 'server',
              },
            });
          }
        }
      },

      initializeAuth: async () => {
        const token = get().token;
        if (!token) {
          set({ isAuthenticated: false, isLoading: false });
          return;
        }

        try {
          set({ isLoading: true });
          await get().getCurrentUser();
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          // getCurrentUser now handles error differentiation (401 vs others)
          // so we just need to ensure loading is set to false
          set({ isLoading: false });
        }
      },

      clearError: () => set({ error: null }),

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: AuthError) => set({ error }),
    }),
    {
      name: 'auth-store',
    }
  )
);

// Custom hook wrapper
export const useAuth = () => {
  const store = useAuthStore();
  return store;
};

export default useAuthStore;
